import { useState, useEffect, useCallback } from 'react';
import {
  View, Text, StyleSheet, ScrollView,
  TouchableOpacity, TextInput, Alert, Platform,
} from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import PaywallGate from '@/components/PaywallGate';
import SkeletonRow from '@/components/SkeletonRow';
import { supabase } from '@/lib/supabase';
import { fetchBaselineStatus } from '@/lib/baselineClient';
import type { BaselineStatus } from '@/lib/baselineGate';
import { fetchStrengthFreshness, type StrengthFreshness } from '@/lib/strengthFreshness';
import { fetchCoachNotes, addCoachNote, deleteCoachNote, type CoachNote } from '@/lib/coachNotes';

// ── Types ─────────────────────────────────────────────────────

interface ClientProfile {
  id: string;
  displayName: string;
  handle: string | null;
}

interface ClientDetailData {
  profile: ClientProfile;
  baseline: BaselineStatus | null;
  baselineError: boolean;
  freshness: StrengthFreshness | null;
  freshnessError: boolean;
  workouts7d: number | null;
  workouts30d: number | null;
}

// ── Main Screen ───────────────────────────────────────────────

export default function TrainerClientDetailScreen() {
  const { clientId } = useLocalSearchParams<{ clientId: string }>();
  const { theme } = useTheme();
  const { profile } = useAuth();
  const c = theme.colors;
  const router = useRouter();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ClientDetailData | null>(null);
  const [notes, setNotes] = useState<CoachNote[]>([]);
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);

    // Resolve client profile
    let clientProfile: ClientProfile = { id: clientId, displayName: 'Client', handle: null };
    try {
      const { data: pf } = await supabase
        .from('profiles')
        .select('id, handle, display_name')
        .eq('id', clientId)
        .maybeSingle();
      if (pf) {
        clientProfile = {
          id: (pf as any).id,
          displayName: (pf as any).display_name ?? 'Client',
          handle: (pf as any).handle ?? null,
        };
      }
    } catch {
      // neutral fallback
    }

    // Baseline
    let baseline: BaselineStatus | null = null;
    let baselineError = false;
    try {
      baseline = await fetchBaselineStatus(clientId);
    } catch {
      baselineError = true;
    }

    // Freshness
    let freshness: StrengthFreshness | null = null;
    let freshnessError = false;
    if (clientProfile.handle) {
      try {
        freshness = await fetchStrengthFreshness(supabase, clientProfile.handle);
      } catch {
        freshnessError = true;
      }
    } else {
      freshnessError = true;
    }

    // Workouts
    let workouts7d: number | null = null;
    let workouts30d: number | null = null;
    try {
      const now = new Date();
      const d7 = new Date(now);
      d7.setDate(d7.getDate() - 7);
      const d30 = new Date(now);
      d30.setDate(d30.getDate() - 30);

      const { count: c7 } = await supabase
        .from('workouts')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', clientId)
        .gte('performed_at', d7.toISOString());
      workouts7d = c7 ?? 0;

      const { count: c30 } = await supabase
        .from('workouts')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', clientId)
        .gte('performed_at', d30.toISOString());
      workouts30d = c30 ?? 0;
    } catch {
      // RLS may block
    }

    setData({ profile: clientProfile, baseline, baselineError, freshness, freshnessError, workouts7d, workouts30d });

    // Load coach notes
    if (profile?.id && clientId) {
      try {
        const coachNotes = await fetchCoachNotes(profile.id, clientId);
        setNotes(coachNotes);
      } catch {
        // notes may not be available
      }
    }

    setLoading(false);
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  return (
    <PaywallGate requiredTier="coach_pro">
      <ScrollView style={[styles.container, { backgroundColor: c.background }]}>
        {/* Back button */}
        <TouchableOpacity onPress={() => router.back()} style={styles.backBtn}>
          <Text style={[styles.backText, { color: c.accent }]}>← Back</Text>
        </TouchableOpacity>

        {loading ? (
          <SkeletonRow rows={4} />
        ) : data ? (
          <>
            {/* Header */}
            <Text style={[styles.title, { color: c.text }]}>
              {data.profile.displayName}
            </Text>
            {data.profile.handle && (
              <Text style={[styles.handle, { color: c.textSecondary }]}>
                @{data.profile.handle}
              </Text>
            )}

            {/* Baseline Status */}
            <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
              <Text style={[styles.cardTitle, { color: c.text }]}>Baseline Status</Text>
              {data.baselineError ? (
                <Text style={[styles.cardValue, { color: c.textSecondary }]}>Unavailable</Text>
              ) : data.baseline ? (
                <>
                  <Row label="Complete" value={data.baseline.isComplete ? 'Yes' : 'No'} c={c} />
                  <Row
                    label="Lifts recorded"
                    value={`${data.baseline.liftsLoggedEver}/5`}
                    c={c}
                  />
                </>
              ) : (
                <Text style={[styles.cardValue, { color: c.textSecondary }]}>No data</Text>
              )}
            </View>

            {/* Freshness */}
            <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
              <Text style={[styles.cardTitle, { color: c.text }]}>Freshness</Text>
              {data.freshnessError ? (
                <Text style={[styles.cardValue, { color: c.textSecondary }]}>Unavailable</Text>
              ) : data.freshness ? (
                <>
                  <Row label="Band" value={data.freshness.freshnessBand} c={c} />
                  {data.freshness.freshnessDays != null && (
                    <Row label="Days since last lift" value={String(data.freshness.freshnessDays)} c={c} />
                  )}
                </>
              ) : (
                <Text style={[styles.cardValue, { color: c.textSecondary }]}>No data</Text>
              )}
            </View>

            {/* Recent Workouts */}
            <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
              <Text style={[styles.cardTitle, { color: c.text }]}>Recent Workouts</Text>
              <Row
                label="Last 7 days"
                value={data.workouts7d != null ? String(data.workouts7d) : 'Unavailable'}
                c={c}
              />
              <Row
                label="Last 30 days"
                value={data.workouts30d != null ? String(data.workouts30d) : 'Unavailable'}
                c={c}
              />
            </View>

            {/* Coach Notes */}
            <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
              <Text style={[styles.cardTitle, { color: c.text }]}>Notes</Text>

              {/* Add note input */}
              <View style={styles.noteInputRow}>
                <TextInput
                  style={[styles.noteInput, { color: c.text, borderColor: c.border, backgroundColor: c.background }]}
                  placeholder="Add a note..."
                  placeholderTextColor={c.textSecondary}
                  value={newNote}
                  onChangeText={setNewNote}
                  multiline
                  maxLength={1000}
                />
                <TouchableOpacity
                  style={[styles.noteAddBtn, { backgroundColor: c.accent, opacity: savingNote || !newNote.trim() ? 0.5 : 1 }]}
                  disabled={savingNote || !newNote.trim()}
                  onPress={async () => {
                    if (!profile?.id || !clientId || !newNote.trim()) return;
                    setSavingNote(true);
                    // Use first org_id from notes or empty string (RLS will enforce)
                    const orgId = notes[0]?.org_id ?? '';
                    const saved = await addCoachNote(orgId, profile.id, clientId, newNote);
                    if (saved) {
                      setNotes((prev) => [saved, ...prev]);
                      setNewNote('');
                    } else {
                      const msg = 'Could not save note. You may need an active coaching link.';
                      Platform.OS === 'web' ? alert(msg) : Alert.alert('Error', msg);
                    }
                    setSavingNote(false);
                  }}>
                  <Text style={[styles.noteAddText, { color: c.text }]}>
                    {savingNote ? '...' : 'Add'}
                  </Text>
                </TouchableOpacity>
              </View>

              {/* Notes list */}
              {notes.length === 0 ? (
                <Text style={[styles.cardValue, { color: c.textSecondary, marginTop: 8 }]}>
                  No notes yet.
                </Text>
              ) : (
                notes.map((n) => (
                  <View key={n.id} style={[styles.noteItem, { borderColor: c.border }]}>
                    <Text style={[styles.noteText, { color: c.text }]}>{n.note}</Text>
                    <View style={styles.noteFooter}>
                      <Text style={[styles.noteDate, { color: c.textSecondary }]}>
                        {new Date(n.created_at).toLocaleDateString()}
                      </Text>
                      <TouchableOpacity
                        onPress={async () => {
                          const ok = await deleteCoachNote(n.id);
                          if (ok) setNotes((prev) => prev.filter((x) => x.id !== n.id));
                        }}>
                        <Text style={{ color: c.error, fontSize: 12 }}>Delete</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                ))
              )}
            </View>
          </>
        ) : (
          <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
            <Text style={[styles.cardValue, { color: c.textSecondary }]}>Client data unavailable.</Text>
          </View>
        )}
      </ScrollView>
    </PaywallGate>
  );
}

// ── Row helper ────────────────────────────────────────────────

function Row({ label, value, c }: { label: string; value: string; c: any }) {
  return (
    <View style={styles.row}>
      <Text style={[styles.rowLabel, { color: c.textSecondary }]}>{label}</Text>
      <Text style={[styles.rowValue, { color: c.text }]}>{value}</Text>
    </View>
  );
}

// ── Styles ────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: { flex: 1, padding: 20 },
  backBtn: { marginBottom: 12 },
  backText: { fontSize: 15, fontWeight: '600' },
  loadingBox: {
    borderWidth: 1, borderRadius: 2, padding: 16,
    alignItems: 'center', justifyContent: 'center', minHeight: 60,
  },
  title: { fontSize: 22, fontWeight: '700' },
  handle: { fontSize: 14, marginTop: 2, marginBottom: 16 },
  card: { borderWidth: 1, borderRadius: 2, padding: 14, marginBottom: 10 },
  cardTitle: { fontSize: 14, fontWeight: '700', marginBottom: 6 },
  cardValue: { fontSize: 15, fontWeight: '600' },
  row: {
    flexDirection: 'row', justifyContent: 'space-between',
    alignItems: 'center', paddingVertical: 4,
  },
  rowLabel: { fontSize: 13, fontWeight: '500' },
  rowValue: { fontSize: 13, fontWeight: '600' },
  noteInputRow: { flexDirection: 'row', gap: 8, marginBottom: 8 },
  noteInput: {
    flex: 1, borderWidth: 1, borderRadius: 2, padding: 10,
    fontSize: 14, minHeight: 40, maxHeight: 100,
  },
  noteAddBtn: {
    paddingHorizontal: 16, paddingVertical: 10, borderRadius: 2,
    justifyContent: 'center', alignItems: 'center',
  },
  noteAddText: { fontSize: 14, fontWeight: '700' },
  noteItem: { borderTopWidth: 1, paddingTop: 8, marginTop: 8 },
  noteText: { fontSize: 14, lineHeight: 20 },
  noteFooter: {
    flexDirection: 'row', justifyContent: 'space-between',
    alignItems: 'center', marginTop: 4,
  },
  noteDate: { fontSize: 11 },
});
